import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KOKORO_LANGUAGES,
  KOKORO_VOICES,
  defaultVoiceFor,
  loadTtsConfig,
  synthesizeSpeech,
  voicesForLanguage,
} from '@/lib/kokoroTts';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useAuth } from '@/hooks/useAuth';
import { primeMerchantVoice } from '@/hooks/useMerchantVoice';

/**
 * Paramètres → Assistant : voix préférée du commerce.
 * Toutes les réponses de l'assistant (page complète + bulle flottante)
 * sont ensuite lues avec cette voix.
 */
export default function AssistantVoiceSettings() {
  const { user } = useAuth();
  const { profile, loading, save } = useCompanyProfile();
  const [lang, setLang] = useState('fr');
  const [voice, setVoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    loadTtsConfig().then((cfg) => setEnabled(cfg.enabled)).catch(() => setEnabled(true));
  }, []);

  useEffect(() => {
    if (loading) return;
    const stored = profile.tts_voice || '';
    setVoice(stored);
    if (stored) {
      const found = KOKORO_VOICES.find((v) => v.id === stored);
      if (found) setLang(found.lang);
    }
  }, [loading, profile.tts_voice]);

  const voices = useMemo(() => voicesForLanguage(lang), [lang]);

  const handleLanguage = (code: string) => {
    setLang(code);
    const first = voicesForLanguage(code)[0];
    if (first) setVoice(first.id);
  };

  const preview = async () => {
    setBusy(true);
    setError(null);
    try {
      const text = 'Bonjour, je suis votre assistant Zifek. Voici ma voix pour votre commerce.';
      const { url } = await synthesizeSpeech({ text, voice: voice || defaultVoiceFor(lang), speed: 1 });
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      await audioRef.current.play().catch(() => {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lecture impossible.');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await save({ tts_voice: voice });
      primeMerchantVoice(user.id, voice);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
      <h3 className="text-sm font-semibold text-foreground-700 mb-1">Voix de l&apos;assistant</h3>
      <p className="text-xs text-foreground-500 mb-5">
        Choisissez la voix utilisée pour lire les réponses de votre assistant sur toute la plateforme.
      </p>

      {!enabled && (
        <div className="mb-5 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 text-xs text-amber-700">
          <i className="ri-volume-mute-line mt-0.5 flex-shrink-0"></i>
          <span>
            La lecture audio est actuellement désactivée par l&apos;administrateur de la plateforme.
            Votre choix de voix est conservé et s&apos;appliquera dès sa réactivation.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">Langue</label>
          <div className="relative">
            <select
              value={lang}
              onChange={(e) => handleLanguage(e.target.value)}
              className="w-full h-10 px-3 pr-8 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
            >
              {KOKORO_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-xs"></i>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">Voix</label>
          <div className="relative">
            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              className="w-full h-10 px-3 pr-8 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
            >
              <option value="">Automatique ({defaultVoiceFor(lang)})</option>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label} · {v.gender === 'F' ? 'Féminine' : 'Masculine'}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-xs"></i>
          </div>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line"></i>{error}
        </p>
      )}

      <div className="flex items-center gap-3 mt-5 flex-wrap">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i>Sauvegarde...</> : <><i className="ri-check-line"></i>Enregistrer la voix</>}
        </button>
        <button
          type="button"
          onClick={preview}
          disabled={busy || !enabled}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 hover:text-primary-700 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {busy ? <><i className="ri-loader-4-line animate-spin"></i>Génération…</> : <><i className="ri-play-circle-line"></i>Écouter un aperçu</>}
        </button>
        {saved && (
          <span className="text-sm text-accent-600 flex items-center gap-1">
            <i className="ri-checkbox-circle-line"></i>Voix enregistrée
          </span>
        )}
      </div>

      <p className="text-[11px] text-foreground-400 mt-4 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
        L&apos;arabe et la darija ne sont pas encore couverts par le moteur vocal : la lecture retombe sur le français.
      </p>
    </div>
  );
}